
import { CheckCircle2 } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { PALETTE_FOREST, PALETTE_MINT } from '../tokens';
import type { FieldConfig } from '../types';
import type { QuestionnaireInput } from '../schema';

/** Per-customer-type display flourishes the backend doesn't ship. */
const TYPE_DECORATION: Record<string, { icon: string; description: string }> = {
  salaried: {
    icon:        '💼',
    description: 'You receive a fixed salary from an employer',
  },
  professional: {
    icon:        '🧑‍💻',
    description: 'Freelancer, consultant, doctor, lawyer, agency, etc.',
  },
  business_owner: {
    icon:        '🏢',
    description: 'You run a registered business or company',
  },
  others: {
    icon:        '🌿',
    description: 'NRI, retired, society, trust, or something else',
  },
};

interface CustomerTypeStepProps {
  /** The `customer_type_field` from the questionnaire-config response. */
  field:    FieldConfig;
  control:  ReturnType<typeof useForm<QuestionnaireInput>>['control'];
  error?:   string;
}

export function CustomerTypeStep({ field, control, error }: CustomerTypeStepProps) {
  const options = field.options ?? [];

  return (
    <div>
      <h2 className="text-xl font-semibold text-foreground mb-1">
        {field.label || 'About you'}
        <span className="text-destructive ml-1" aria-label="required">*</span>
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        This helps us to curate a personalized experience for you!
      </p>

      <Controller
        name="customer_type"
        control={control}
        render={({ field: rhfField }) => (
          <div className="space-y-3">
            {options.map((opt) => {
              const selected   = rhfField.value === opt.value;
              const decoration = TYPE_DECORATION[opt.value];
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => rhfField.onChange(opt.value)}
                  className="w-full text-left rounded-xl border-2 px-4 py-4 flex items-center gap-4 transition-all focus:outline-none"
                  style={{
                    borderColor:     selected ? PALETTE_FOREST : 'hsl(var(--border))',
                    backgroundColor: selected ? PALETTE_MINT   : 'transparent',
                  }}
                >
                  {decoration?.icon && (
                    <span className="text-2xl shrink-0">{decoration.icon}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-semibold"
                      style={{ color: selected ? PALETTE_FOREST : 'hsl(var(--foreground))' }}
                    >
                      {opt.label}
                    </p>
                    {decoration?.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {decoration.description}
                      </p>
                    )}
                  </div>
                  {selected && (
                    <CheckCircle2
                      className="h-5 w-5 shrink-0"
                      style={{ color: PALETTE_FOREST }}
                      aria-hidden
                    />
                  )}
                </button>
              );
            })}
            {error && <p className="text-xs text-destructive mt-1">{error}</p>}
          </div>
        )}
      />
    </div>
  );
}
